/* ==========================================================
   changeCam.js — maneja el cambio de cámara/pista de vídeo
   ========================================================== */

import { captureAndShowFrame, removeCapturedFrame } from '../render/screen-actions';
import { selectMenuItem } from './menu-handlers';
// import { changeCameraTrack } from '../../../sync-x/client/js/client';

let player               = null;
let wasMuted             = null;
let savedCameraPosition  = null;

/* ----------------------------------------------------------
   Helper: espera al primer frame de la pista recién elegida
   ---------------------------------------------------------- */
function waitForFirstFrame(videoEl, cb) {
  if (!videoEl?.requestVideoFrameCallback) {
    // Fallback para navegadores antiguos → ~100 ms
    return setTimeout(cb, 100);
  }
  videoEl.requestVideoFrameCallback(() => cb());
}

/* ==========================================================
   Núcleo
   ========================================================== */
function changeCamCore(requestedCamera, cameraItems, isMaster) {

  /* ============================================================== */
  /* 1️⃣  WEBRTC (mediasoup)                                         */
  /* ============================================================== */
  if (window.isWebRTC) {

    /* 1-A  Pide la nueva pista al servidor ------------------------ */
    const trackId = window.webRtcVideoTrackIds?.[requestedCamera] ?? requestedCamera;
    if (typeof window.msSwitchTrack === 'function') {
      window.msSwitchTrack('video', trackId);
    }

    /* 1-B  Cuando llegue el PRIMER frame ajusta la proyección ----- */
    player = videojs.getAllPlayers()[0];
    const videoEl       = player.tech_.el();
    const newProjection = window.videoTypes?.[requestedCamera] ?? 'NONE';

    waitForFirstFrame(videoEl, () => {
      if (videojs.browser.IS_IOS) {
        if (player.vr?.() && player.vr().currentProjection_ !== newProjection) {
          player.vr().setProjection(
            (newProjection === '2D' || newProjection === 'NONE') ? 'NONE' : newProjection
          );
          player.vr().init();
        }
      } else if (player.xr?.() && player.xr().options.projection !== newProjection) {
        player.xr().options.projection = newProjection;
        try { player.xr().init(); } catch { /* esclavo Sync-X: ignora */ }
      }

      /* UX extra: cursor y entorno -------------------------------- */
      player.el().classList.toggle(
        'force-default-cursor',
        newProjection === '2D' || newProjection === 'NONE'
      );
      if (!videojs.browser.IS_IOS &&
          typeof window.updateProjectionEnvironment === 'function') {
        window.updateProjectionEnvironment(newProjection);
      }
    });

    /* 1-C  Marca la cámara activa en el menú ---------------------- */
    if (cameraItems) selectMenuItem(cameraItems, 'selectedCameraIndex');

    return;   // ⬅️  Termina la rama WebRTC
  }

  /* ============================================================== */
  /* 2️⃣  DASH / HLS / PLAYLIST LOCAL                                */
  /* ============================================================== */

  if (isMaster && typeof changeCameraTrack === 'function') {
    changeCameraTrack(requestedCamera);
  }

  player = videojs.getAllPlayers()[0];

  const currentProjection = videojs.browser.IS_IOS
    ? player.vr().currentProjection_
    : player.xr().options.projection;

  const playerCamPos = videojs.browser.IS_IOS
    ? player.vr().camera.position
    : player.xr().camera.position;

  if ((currentProjection !== '2D' && currentProjection !== 'NONE') &&
      (window.videoTypes[requestedCamera] === '2D' || window.videoTypes[requestedCamera] === 'NONE')) {
    savedCameraPosition = playerCamPos;
  }

  /* ------------------ Silenciar temporalmente IOS CAPTURE FRAME------------------- */
  /*if (!window.isDashSource) {
      wasMuted = player.muted();
      player.muted(true);
  }*/

  /* ------------ Recordamos tiempo y audio track actual ---------- */
  let customCurrentTime = null;
  if (!window.isDashSource) {
    customCurrentTime = player.currentTime();
    for (let i = 0; i < player.audioTracks().length; i++) {
      if (player.audioTracks()[i].enabled) {
        const label = player.audioTracks()[i].label || `Audio ${i + 1}`;
        localStorage.setItem('selectedAudioLabel', label);
        localStorage.setItem('selectedQualityIndex', '0');
        break;
      }
    }
  }

  /* 2-A  Actualiza proyección VR/XR ------------------------------ */
  if (typeof player.vr === 'function') {
    if (player.vr().currentProjection_ !== window.videoTypes[requestedCamera]) {
      const proj = window.videoTypes[requestedCamera];
      player.vr().setProjection((proj === '2D' || proj === 'NONE') ? 'NONE' : proj);
    }
    if (window.isDashSource) player.vr().init();
  } else if (typeof player.xr === 'function') {
    if (player.xr().options.projection !== window.videoTypes[requestedCamera]) {
      player.xr().options.projection = window.videoTypes[requestedCamera];
    }
  }

  if (!window.isDashSource) {
    //captureAndShowFrame(player, currentProjection);
  }

  /* 2-B  Selección de track en DASH o playlist -------------------- */
  if (window.isDashSource) {
    const mp = window.dashMediaPlayer;
    if (mp?.getTracksFor) {
      const tracks = mp.getTracksFor('video');
      if (requestedCamera >= 0 && requestedCamera < tracks.length) {
        mp.setCurrentTrack(tracks[requestedCamera]);
      } else {
        console.warn('Requested camera index is out of bounds.');
      }
    } else {
      console.warn('dashMediaPlayer is not available.');
    }
  } else if (!window.isWebRTC) {
    player.playlist.currentItem(requestedCamera);
  }

  /* 2-C  Subtítulos, audio y estado tras cargar ------------------- */
  if (!window.isDashSource) {
    player.one('loadedmetadata', () => {
      const selectedSubs = localStorage.getItem('selectedSubtitleLabel') || 'Disabled';
      const tracks = player.textTracks();
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === 'subtitles') {
          tracks[i].mode =
            (selectedSubs === 'Disabled')
              ? 'disabled'
              : (tracks[i].label === selectedSubs ? 'showing' : 'disabled');
        }
      }
      player.controlBar.getChild('SubtitlesMenuButton')?.updateMenuItems(tracks);
    });
  }

  /* 2-D  Restaura tiempo y UI ------------------------------------ */
  if (!window.isDashSource) player.currentTime(customCurrentTime);

  player.userActive(false);

  player.one('seeked', () => {

    if (isMaster && cameraItems) selectMenuItem(cameraItems, 'selectedCameraIndex');

    if ((currentProjection !== '2D' && currentProjection !== 'NONE') &&
        (window.videoTypes[requestedCamera] !== '2D' && window.videoTypes[requestedCamera] !== 'NONE')) {
      if (!videojs.browser.IS_IOS) {
        player.xr().camera.position.set(playerCamPos.x, playerCamPos.y, playerCamPos.z);
      }
    }

    player.one('progress', () => {
	  if (!window.isDashSource) {
        const selectedAudio = localStorage.getItem('selectedAudioLabel');
        const aTracks = player.audioTracks();
        if (selectedAudio) {
          for (let i = 0; i < aTracks.length; i++) {
            const lbl = aTracks[i].label || `Audio ${i + 1}`;
            aTracks[i].enabled = lbl === selectedAudio;
          }
        }
	    /* IOS REMOVEFRAME */
        //if (!wasMuted) player.muted(false);
      }

      if (videojs.browser.IS_IOS) {
        document.querySelector('.vjs-text-track-display')?.classList.remove('vjs-hidden');
      }

      const waitPlaybackOK = () => {
        const t   = player.currentTime();
        const buf = player.buffered();
        if (t > customCurrentTime + 0.0 && !player.paused() &&
            player.readyState() >= 4 && buf.length && buf.end(0) > t) {
          /* IOS REMOVEFRAME */
		  //removeCapturedFrame();
          player.off('timeupdate', waitPlaybackOK);
        }
      };
      player.on('timeupdate', waitPlaybackOK);
    });
  });

  /* 2-E  Cursor / entorno ---------------------------------------- */
  const forceDefault = (window.videoTypes[requestedCamera] === 'NONE' || window.videoTypes[requestedCamera] === '2D');
  player.el().classList.toggle('force-default-cursor', forceDefault);

  if (!videojs.browser.IS_IOS) {
    window.updateProjectionEnvironment?.(window.videoTypes[requestedCamera]);
  }

  /* 2-F  Fullscreen en iOS tras cambio de item -------------------- */
  if (!window.isDashSource) {
    player.one('playlistitem', () => {
      player.one('loadeddata', () => {
        if (videojs.browser.IS_IOS) {
          player.controlBar.fullscreenToggle.show();
          document.querySelector('.vjs-fullscreen-control')?.classList.remove('vjs-hidden');
        }
      });
    });
  }

  /* 2-G  Ajuste extra para XR en DASH ----------------------------- */
  if (window.isDashSource && typeof player.xr === 'function' &&
      currentProjection !== player.xr().options.projection) {

    const trackChangeHandler = (e) => {
      if (e.mediaType === 'video') {
        const playbackProgressHandler = () => {
          try { player.xr().init(); } catch {}
          if ((currentProjection === '2D' || currentProjection === 'NONE') &&
              (window.videoTypes[requestedCamera] !== '2D' && window.videoTypes[requestedCamera] !== 'NONE')) {
            if (savedCameraPosition && !videojs.browser.IS_IOS) {
              player.xr().camera.position.copy(savedCameraPosition);
            }
          }
          window.dashMediaPlayer.off(dashjs.MediaPlayer.events.PLAYBACK_PROGRESS, playbackProgressHandler);
          window.dashMediaPlayer.off(dashjs.MediaPlayer.events.TRACK_CHANGE_RENDERED, trackChangeHandler);
        };
        window.dashMediaPlayer.on(dashjs.MediaPlayer.events.PLAYBACK_PROGRESS, playbackProgressHandler);
      }
    };
    window.dashMediaPlayer.on(dashjs.MediaPlayer.events.TRACK_CHANGE_RENDERED, trackChangeHandler);
  }

  /* 2-H  ¡Play! --------------------------------------------------- */
  player.play();
}

/* ==========================================================
   API pública
   ========================================================== */

export function changeCam           (requestedCamera, cameraItems) { changeCamCore(requestedCamera, cameraItems, true);  }
export function changeCamSyncxSlave (requestedCamera)              { changeCamCore(requestedCamera, null,        false); }

window.changeCam           = changeCam;
window.changeCamSyncxSlave = changeCamSyncxSlave;